import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import SalePaymentPreset from "@/components/SalePaymentPreset";
import { activityLabel } from "@/lib/activityLabels";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { API, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { formatPrice, UNIT_STATUSES } from "@/lib/constants";
import { toast } from "sonner";
import StatusBadge from "@/components/StatusBadge";
import ImageUploadField from "@/components/ImageUploadField";
import { Copy, ExternalLink, Bookmark, Wallet, ShoppingCart, ArrowLeft, RotateCcw, Edit2, History, Flame, Trash2, MessageCircle, Printer } from "lucide-react";
import {
  UNIT_TYPES, ROOM_TYPES, ASPECTS, VIEWS, FLOOR_POSITIONS, CURRENCIES,
} from "@/lib/constants";

const KNOWN_PHONE_COUNTRY_CODES = ["971", "966", "994", "90", "49", "44", "31", "33", "1", "7"];

export default function UnitDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [unit, setUnit] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [interests, setInterests] = useState([]);
  const [notes, setNotes] = useState([]);
  const [offers, setOffers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [users, setUsers] = useState([]);
  const [editBlocks, setEditBlocks] = useState([]);
  const [editFloors, setEditFloors] = useState([]);
  const [context, setContext] = useState(null);
  const [activity, setActivity] = useState([]);
  const [showReserve, setShowReserve] = useState(false);
  const [showDeposit, setShowDeposit] = useState(false);
  const [showSale, setShowSale] = useState(false);
  const [showInterest, setShowInterest] = useState(false);
  const [showCancelSale, setShowCancelSale] = useState(false);
  const [showCancelReservation, setShowCancelReservation] = useState(false);
  const [showCancelDeposit, setShowCancelDeposit] = useState(false);
  const [showEditUnit, setShowEditUnit] = useState(false);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [offerBusy, setOfferBusy] = useState(false);
  const [preparedOfferId, setPreparedOfferId] = useState("");
  const [expandedOfferId, setExpandedOfferId] = useState("");
  const [expandedLists, setExpandedLists] = useState({});
  const [mediaOpen, setMediaOpen] = useState(false);
  const [mediaViewer, setMediaViewer] = useState(null);
  const [activeSale, setActiveSale] = useState(null);
  const [reserveForm, setReserveForm] = useState({ customer_id: "", valid_until: "", note: "" });
  const [depositForm, setDepositForm] = useState({ customer_id: "", amount: "", currency: "TRY", note: "" });
  const [saleForm, setSaleForm] = useState({ customer_id: "", sale_price: "", currency: "TRY", payment_type: "Peşin", note: "" });
  const [interestForm, setInterestForm] = useState({ customer_id: "", status: "İlgileniyor", note: "", next_followup_at: "", followup_subject: "" });
  const [cancelReason, setCancelReason] = useState("");
  const [cancelResForm, setCancelResForm] = useState({ reason: "" });
  const [cancelDepForm, setCancelDepForm] = useState({ reason: "", description: "", refunded: false, new_unit_status: "Satışta", new_customer_status: "" });
  const [editForm, setEditForm] = useState({});
  const [offerForm, setOfferForm] = useState({
    customer_id: "", customer_name: "", phone_country_code: "90", phone_local: "",
    offer_price: "", include_price: true, valid_until: "", message: "",
    conversation_note: "", next_followup_at: "", include_pdf_link: false,
  });

  const isAdmin = user?.role === "admin";
  const isManager = user?.role === "manager";
  const canCancelDeposit = isAdmin || isManager;
  const canEdit = isAdmin || isManager;
  const localPdfLink = ["localhost", "127.0.0.1"].includes(new URL(API || "/", window.location.origin).hostname);

  const load = useCallback(async () => {
    setLoadError("");
    try {
      const { data } = await api.get(`/units/${id}`, { timeout: 15000 });
      setUnit(data);
      setSaleForm((f) => ({ ...f, sale_price: data.price, currency: data.currency }));
      setDepositForm((f) => ({ ...f, currency: data.currency }));
      setEditForm({
        unit_no: data.unit_no || "",
        block_id: data.block_id || "",
        floor_id: data.floor_id || "",
        unit_type: data.unit_type, room_type: data.room_type, layout_type: data.layout_type || "",
        net_area: data.net_area, gross_area: data.gross_area,
        aspect: data.aspect, view: data.view,
        floor_position: data.floor_position, price: data.price,
        currency: data.currency, status: data.status,
        sales_agent_id: data.sales_agent_id || "",
        plan_url: data.plan_url || "",
        image_urls_text: (data.image_urls || []).join("\n"),
        video_url: data.video_url || "",
        seating_plan_url: data.seating_plan_url || "",
        note: data.note || "",
      });
    } catch (e) { const message = e.response ? formatApiError(e) : "Sunucu yanıtı alınamadı. Lütfen tekrar deneyin."; setLoadError(message); toast.error(message); }
  }, [id]);

  const loadContext = useCallback(() => api.get(`/units/${id}/context`).then(({ data }) => setContext(data)).catch(() => {}), [id]);
  const loadActivity = useCallback(() => api.get(`/units/${id}/activity?limit=10`).then(({ data }) => setActivity(data)), [id]);
  const loadInterests = useCallback(() => api.get(`/units/${id}/interests`).then(({ data }) => setInterests(data)), [id]);
  const loadNotes = useCallback(() => api.get(`/units/${id}/notes`).then(({ data }) => setNotes(data)), [id]);
  const loadOffers = useCallback(() => api.get(`/units/${id}/offers`).then(({ data }) => setOffers(data)).catch(() => setOffers([])), [id]);
  const loadCustomers = useCallback(() => api.get("/customers").then(({ data }) => setCustomers(data)), []);
  const loadUsers = useCallback(() => api.get("/company/users").then(({ data }) => setUsers(data)), []);
  const loadActiveSale = useCallback(async () => {
    if (unit?.status !== "Satıldı") { setActiveSale(null); return; }
    const { data } = await api.get("/sales");
    const s = data.find((x) => x.unit_id === id && x.status === "Aktif");
    setActiveSale(s || null);
  }, [id, unit?.status]);

  useEffect(() => {
    let active = true;
    for (const fetchData of [load, loadInterests, loadNotes, loadOffers, loadCustomers, loadUsers, loadContext, loadActivity]) {
      fetchData().catch(e => { if (active) toast.error(formatApiError(e)); });
    }
    return () => { active = false; };
  }, [load, loadInterests, loadNotes, loadOffers, loadCustomers, loadUsers, loadContext, loadActivity]);
  useEffect(() => { loadActiveSale().catch(e => toast.error(formatApiError(e))); }, [loadActiveSale]);
  useEffect(() => {
    if (!showEditUnit || !unit?.project_id) return;
    api.get(`/projects/${unit.project_id}/blocks`).then(({ data }) => setEditBlocks(data)).catch(() => setEditBlocks([]));
  }, [showEditUnit, unit?.project_id]);
  useEffect(() => {
    if (!showEditUnit || !editForm.block_id) {
      setEditFloors([]);
      return;
    }
    api.get(`/blocks/${editForm.block_id}/floors`).then(({ data }) => setEditFloors(data)).catch(() => setEditFloors([]));
  }, [showEditUnit, editForm.block_id]);

  // When opening Deposit modal: pre-fill locked customer + currency
  useEffect(() => {
    if (showDeposit && context?.locked_customer_id) {
      setDepositForm((f) => ({ ...f, customer_id: context.locked_customer_id, currency: context.currency || f.currency }));
    }
  }, [showDeposit, context]);
  // When opening Sale modal: pre-fill locked customer + price
  useEffect(() => {
    if (showSale && context?.locked_customer_id) {
      setSaleForm((f) => ({ ...f, customer_id: context.locked_customer_id }));
    }
  }, [showSale, context]);

  const copyUnitLink = () => {
    const link = getUnitPresentationLink();
    navigator.clipboard.writeText(link);
    toast.success("Bölüm sunum linki kopyalandı");
  };

  const getUnitPresentationLink = () => `${window.location.origin}/p/units/${id}`;

  const defaultOfferValidUntil = () => {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    return d.toISOString().slice(0, 10);
  };

  const parseOfferPhone = (rawPhone) => {
    const raw = (rawPhone || "").trim();
    const digits = raw.replace(/\D/g, "");
    if (!digits) return { phone_country_code: "90", phone_local: "" };

    if (raw.startsWith("+")) {
      const match = KNOWN_PHONE_COUNTRY_CODES.find((code) => digits.startsWith(code));
      if (match) {
        return {
          phone_country_code: match,
          phone_local: digits.slice(match.length).replace(/^0+/, ""),
        };
      }
      const guessedCodeLength = digits.length > 12 ? 3 : 2;
      return {
        phone_country_code: digits.slice(0, guessedCodeLength),
        phone_local: digits.slice(guessedCodeLength).replace(/^0+/, ""),
      };
    }

    if (digits.startsWith("90") && digits.length > 10) {
      return { phone_country_code: "90", phone_local: digits.slice(2).replace(/^0+/, "") };
    }
    if (digits.startsWith("0")) {
      return { phone_country_code: "90", phone_local: digits.slice(1) };
    }
    return { phone_country_code: "90", phone_local: digits };
  };

  const getOfferPhoneDigits = (form = offerForm) => {
    const country = (form.phone_country_code || "").replace(/\D/g, "");
    const local = (form.phone_local || "").replace(/\D/g, "").replace(/^0+/, "");
    return local ? `${country}${local}` : "";
  };

  const getOfferPhoneDisplay = (form = offerForm) => {
    const digits = getOfferPhoneDigits(form);
    return digits ? `+${digits}` : "";
  };

  const buildOfferText = (form = offerForm) => {
    const specs = [
      [unit.unit_type, unit.room_type, unit.layout_type].filter(Boolean).join(" "),
      unit.net_area ? `Net ${unit.net_area} m²` : "",
      unit.gross_area ? `Brüt ${unit.gross_area} m²` : "",
      unit.aspect ? `Cephe: ${unit.aspect}` : "",
      unit.view ? `Manzara: ${unit.view}` : "",
    ].filter(Boolean).join(" • ");
    const greeting = form.customer_name?.trim()
      ? `Merhaba ${form.customer_name.trim()},`
      : "Merhaba,";
    return [
      `${greeting} ${unit.project_name} projesindeki ${unit.unit_no} bağımsız bölüm için sunum bilgisini iletiyorum.`,
      "",
      `Proje: ${unit.project_name}`,
      `Konum: ${unit.block_name} / ${unit.floor_name}`,
      specs ? `Özellikler: ${specs}` : "",
      form.include_price ? `Fiyat: ${formatPrice(Number(form.offer_price) || unit.price, unit.currency)}` : "Fiyat bilgisi için satış temsilcinizle iletişime geçebilirsiniz.",
      form.valid_until ? `Teklif geçerlilik tarihi: ${form.valid_until}` : "",
      "",
      `Sunum linki: ${getUnitPresentationLink()}`,
    ].filter((line) => line !== "").join("\n");
  };

  const openOfferModal = () => {
    const next = {
      customer_id: "",
      customer_name: "",
      phone_country_code: "90",
      phone_local: "",
      offer_price: unit.price || 0,
      include_price: true,
      valid_until: defaultOfferValidUntil(),
      message: "",
      conversation_note: "",
      next_followup_at: "",
      include_pdf_link: !localPdfLink,
    };
    next.message = buildOfferText(next);
    setOfferForm(next);
    setPreparedOfferId("");
    setShowOfferModal(true);
  };

  const updateOfferForm = (patch) => {
    setOfferForm((form) => {
      const next = { ...form, ...patch };
      return { ...next, message: buildOfferText(next) };
    });
  };

  const selectOfferCustomer = (customerId) => {
    const selected = customers.find((c) => c.id === customerId);
    const parsedPhone = parseOfferPhone(selected?.phone || "");
    updateOfferForm({
      customer_id: customerId,
      customer_name: selected?.full_name || "",
      ...parsedPhone,
    });
  };

  const logOffer = async (channel = "WhatsApp") => {
    const { data } = await api.post(`/units/${id}/offers`, {
      customer_id: offerForm.customer_id || null,
      customer_name: offerForm.customer_name,
      phone: getOfferPhoneDisplay(),
      offered_price: Number(offerForm.offer_price) || unit.price || 0,
      currency: unit.currency,
      include_price: offerForm.include_price,
      valid_until: offerForm.valid_until,
      channel,
      message: offerForm.message || buildOfferText(),
      conversation_note: offerForm.conversation_note,
      next_followup_at: offerForm.next_followup_at,
      include_pdf_link: offerForm.include_pdf_link,
    });
    loadActivity();
    loadNotes();
    loadOffers();
    loadInterests();
    loadCustomers();
    setPreparedOfferId(data.id);
    return data;
  };

  const confirmOfferSent = async (offerId) => {
    setOfferBusy(true);
    try {
      await api.post(`/offers/${offerId}/mark-sent`);
      await Promise.all([loadOffers(), loadActivity(), loadCustomers(), loadInterests()]);
      setPreparedOfferId("");
      toast.success("Teklif gönderildi olarak kaydedildi");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setOfferBusy(false); }
  };

  const revokeOfferPdf = async (offerId) => {
    if (!window.confirm("Bu teklifin PDF bağlantısını kapatmak istiyor musunuz?")) return;
    setOfferBusy(true);
    try {
      await api.post(`/offers/${offerId}/revoke-pdf`);
      await Promise.all([loadOffers(), loadActivity()]);
      toast.success("PDF bağlantısı kapatıldı");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setOfferBusy(false); }
  };

  const copyOfferText = async () => {
    setOfferBusy(true);
    try {
      const offer = await logOffer("Kopyala");
      const message = offer.message || offerForm.message || buildOfferText();
      try {
        await navigator.clipboard.writeText(message);
      } catch {
        const input = document.createElement("textarea");
        input.value = message;
        input.style.position = "fixed";
        input.style.opacity = "0";
        document.body.appendChild(input);
        input.select();
        const copied = document.execCommand("copy");
        input.remove();
        if (!copied) throw new Error("Teklif kaydedildi ancak panoya kopyalanamadı.");
      }
      toast.success("Teklif hazırlandı ve metin kopyalandı. Gönderdiğinizde onaylayın.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setOfferBusy(false);
    }
  };

  const openWhatsappOffer = async () => {
    const popup = window.open("", "_blank");
    if (!popup) { toast.error("WhatsApp sekmesi açılamadı. Açılır pencerelere izin verin."); return; }
    popup.opener = null;
    setOfferBusy(true);
    try {
      const offer = await logOffer("WhatsApp");
      const phone = getOfferPhoneDigits();
      const text = encodeURIComponent(offer.message || offerForm.message || buildOfferText());
      const url = phone
        ? `https://api.whatsapp.com/send?phone=${phone}&text=${text}`
        : `https://api.whatsapp.com/send?text=${text}`;
      popup.location.replace(url);
      toast.success("WhatsApp açıldı. Mesajı gönderdikten sonra panelde onaylayın.");
    } catch (e) {
      popup.close();
      toast.error(formatApiError(e));
    } finally {
      setOfferBusy(false);
    }
  };

  const createReservation = async (e) => {
    e.preventDefault();
    try {
      await api.post("/reservations", { ...reserveForm, unit_id: id });
      toast.success("Rezervasyon oluşturuldu");
      setShowReserve(false); load(); loadInterests(); loadContext(); loadActivity();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const createDeposit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/deposits", { ...depositForm, unit_id: id, amount: Number(depositForm.amount) });
      toast.success("Kapora alındı");
      setShowDeposit(false); load(); loadContext(); loadActivity();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const createSale = async (e) => {
    e.preventDefault();
    try {
      await api.post("/sales", { ...saleForm, unit_id: id, sale_price: Number(saleForm.sale_price) });
      toast.success("Satış oluşturuldu");
      setShowSale(false); load(); loadContext(); loadActivity();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const createInterest = async (e) => {
    e.preventDefault();
    try {
      await api.post("/interests", { ...interestForm, unit_id: id });
      toast.success("İlgi kaydı eklendi");
      setShowInterest(false); loadInterests(); loadActivity();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const cancelSale = async (e) => {
    e.preventDefault();
    if (!activeSale) return;
    try {
      await api.post(`/sales/${activeSale.id}/cancel`, { reason: cancelReason });
      toast.success("Satış iptal edildi, bölüm stoğa döndü");
      setShowCancelSale(false); setCancelReason(""); load(); loadActiveSale(); loadContext(); loadActivity();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const cancelReservation = async (e) => {
    e.preventDefault();
    if (!context?.active_reservation) return;
    try {
      await api.post(`/reservations/${context.active_reservation.id}/cancel`, { reason: cancelResForm.reason });
      toast.success("Rezervasyon iptal edildi, bölüm stoğa döndü");
      setShowCancelReservation(false); setCancelResForm({ reason: "" });
      load(); loadContext(); loadActivity();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const cancelDeposit = async (e) => {
    e.preventDefault();
    if (!context?.active_deposits?.length) return;
    const dep = context.active_deposits[0];
    try {
      const { data } = await api.post(`/deposits/${dep.id}/cancel`, cancelDepForm);
      toast.success(`Kapora ${cancelDepForm.refunded ? "iade edildi" : "yakıldı"}, bölüm ${data.unit_status} durumunda`);
      setShowCancelDeposit(false);
      setCancelDepForm({ reason: "", description: "", refunded: false, new_unit_status: "Satışta", new_customer_status: "" });
      load(); loadContext(); loadActivity();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const submitEditUnit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...editForm,
        net_area: Number(editForm.net_area) || 0,
        gross_area: Number(editForm.gross_area) || 0,
        price: Number(editForm.price) || 0,
        sales_agent_id: editForm.sales_agent_id || null,
        image_urls: (editForm.image_urls_text || "").split(/\r?\n/).map((x) => x.trim()).filter(Boolean),
      };
      delete payload.image_urls_text;
      await api.put(`/units/${id}`, payload);
      toast.success("Bağımsız bölüm güncellendi");
      setShowEditUnit(false); load(); loadActivity();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const deleteUnit = async () => {
    if (!window.confirm(`"${unit.unit_no}" bağımsız bölümünü silmek veya pasife almak istiyor musunuz?\n\nTeklif, PDF veya işlem geçmişi varsa kayıt korunur. Aktif rezervasyon, kapora veya satış varsa işlem engellenir.`)) return;
    try {
      const { data } = await api.delete(`/units/${id}`);
      toast.success(data.archived ? "Bağımsız bölüm pasife alındı" : "Bağımsız bölüm silindi");
      if (data.deleted) {
        nav(`/projects/${unit.project_id}`);
      } else {
        load(); loadContext(); loadActivity();
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!unit) return loadError ? <div role="alert" className="space-y-3"><p>Bölüm yüklenemedi. {loadError}</p><button type="button" className="btn-secondary" onClick={load}><RotateCcw size={16} />Tekrar dene</button></div> : <div className="text-slate-500">Yükleniyor…</div>;

  const isLocked = ["Satıldı", "Teslim Edildi"].includes(unit.status);
  const editStructureLocked = ["Rezerve", "Kapora Alındı", "Satıldı", "Teslim Edildi"].includes(unit.status);
  const myCustomers = user?.role === "agent" ? customers.filter((c) => c.assigned_agent_id === user.id) : customers;
  const lockedCustomer = context?.locked_customer_id ? { id: context.locked_customer_id, full_name: context.locked_customer_name } : null;

  // Sale modal calculations
  const depositTotal = context?.deposit_total || 0;
  const listPrice = Number(unit.price) || 0;
  const salePriceNum = Number(saleForm.sale_price) || listPrice;
  const remaining = salePriceNum - depositTotal;
  const lastActivity = activity[0];
  const unitImages = unit.image_urls || [];
  const planLinks = [
    { label: "Daire Planı", url: unit.plan_url },
    { label: "Kat Planı", url: unit.floor_plan_url },
    { label: "Konum Planı", url: unit.seating_plan_url },
  ].filter((item) => item.url);
  const hasMedia = unitImages.length > 0 || unit.video_url || planLinks.length > 0;
  const isListExpanded = (key) => Boolean(expandedLists[key]);
  const toggleList = (key) => setExpandedLists((current) => ({ ...current, [key]: !current[key] }));
  const visibleRecords = (key, records) => (isListExpanded(key) ? records : records.slice(0, 2));
  const recordListClass = (key, records) => (
    isListExpanded(key) && records.length > 5 ? "divide-y divide-slate-100 max-h-[360px] overflow-y-auto" : "divide-y divide-slate-100"
  );

  return (
    <div className="space-y-6" data-testid="unit-detail-page">
      <button onClick={() => nav(-1)} className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
        <ArrowLeft className="w-4 h-4" /> Geri
      </button>

      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">
            {unit.project_name} • {unit.block_name} • {unit.floor_name}
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 font-display" data-testid="unit-title">{unit.unit_no}</h1>
          <div className="mt-2 flex items-center gap-3 flex-wrap">
            <StatusBadge status={unit.status} testid="unit-status-badge" />
            <span className="text-sm text-slate-600">
              {[unit.unit_type, unit.room_type, unit.layout_type].filter(Boolean).join(" • ")}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={copyUnitLink}
                  className="bg-white border border-slate-200 hover:border-slate-300 px-3 py-2 text-sm rounded-lg inline-flex items-center gap-2"
                  data-testid="copy-unit-link">
            <Copy className="w-4 h-4" /> Bağlantıyı Kopyala
          </button>
          <button onClick={openOfferModal}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white px-3 py-2 text-sm rounded-lg inline-flex items-center gap-2"
                  data-testid="whatsapp-offer-link">
            <MessageCircle className="w-4 h-4" /> WhatsApp Teklif
          </button>
          <Link to={`/p/units/${id}`} target="_blank"
                className="bg-amber-500 hover:bg-amber-600 text-white px-3 py-2 text-sm rounded-lg inline-flex items-center gap-2">
            <ExternalLink className="w-4 h-4" /> Sunumda Aç
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {unit.layout_type && <Info l="Plan Tipi" v={unit.layout_type} />}
        <Info l="Net m²" v={`${unit.net_area} m²`} />
        <Info l="Brüt m²" v={`${unit.gross_area} m²`} />
        <Info l="Cephe" v={unit.aspect} />
        <Info l="Manzara" v={unit.view} />
        <Info l="Kat Konumu" v={unit.floor_position} />
        <Info l="Sorumlu" v={unit.sales_agent_name || "—"} />
        <Info l="Fiyat" v={formatPrice(unit.price, unit.currency)} highlight testid="unit-price" />
        <Info l="İlgili Müşteri" v={unit.interest_count || 0} />
      </div>

      {/* Son Aktivite */}
      {lastActivity && (
        <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-start gap-3" data-testid="last-activity">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-amber-50 text-amber-600 shrink-0">
            <History className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Son Aktivite</div>
            <div className="mt-0.5 text-sm text-slate-900 truncate">
              {(lastActivity.created_at || "").slice(0, 19).replace("T", " ")} — {activityLabel(lastActivity.action_type)} — {lastActivity.user_name}
            </div>
            <div className="text-xs text-slate-600 mt-0.5 line-clamp-2">{lastActivity.description}</div>
          </div>
        </div>
      )}

      {/* Active reservation/deposit info card */}
      {(context?.active_reservation || (context?.active_deposits || []).length > 0) && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm" data-testid="active-lock-info">
          {context.active_reservation && (
            <div>
              <b>Aktif rezervasyon:</b> {context.active_reservation.customer_name}
              {context.active_reservation.valid_until && <span className="text-slate-600 ml-2">• Geçerlilik: {context.active_reservation.valid_until.slice(0, 10)}</span>}
            </div>
          )}
          {(context.active_deposits || []).map((d) => (
            <div key={d.id}>
              <b>Aktif kapora:</b> {d.customer_name} — {formatPrice(d.amount, d.currency)} ({(d.deposit_date || "").slice(0, 10)})
            </div>
          ))}
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2 flex-wrap">
        {canEdit && (
          <button onClick={() => setShowEditUnit(true)}
                  className="px-4 py-2 text-sm bg-white border border-slate-200 rounded-lg hover:border-slate-300 inline-flex items-center gap-2"
                  data-testid="edit-unit-btn">
            <Edit2 className="w-4 h-4" /> Düzenle
          </button>
        )}
        {isAdmin && (
          <button onClick={deleteUnit}
                  className="px-4 py-2 text-sm bg-white border border-red-300 text-red-700 hover:bg-red-50 rounded-lg inline-flex items-center gap-2"
                  data-testid="delete-unit-btn">
            <Trash2 className="w-4 h-4" /> Sil / Pasife Al
          </button>
        )}
        <button onClick={() => setShowInterest(true)}
                className="px-4 py-2 text-sm bg-white border border-slate-200 rounded-lg hover:border-slate-300"
                data-testid="add-interest-btn">
          + İlgi Kaydı Ekle
        </button>
        {!isLocked && unit.status !== "Rezerve" && unit.status !== "Kapora Alındı" && (
          <button onClick={() => setShowReserve(true)}
                  className="px-4 py-2 text-sm bg-blue-500 hover:bg-blue-600 text-white rounded-lg inline-flex items-center gap-2"
                  data-testid="reserve-btn">
            <Bookmark className="w-4 h-4" /> Rezerve Et
          </button>
        )}
        {unit.status === "Rezerve" && (
          <button onClick={() => setShowCancelReservation(true)}
                  className="px-4 py-2 text-sm bg-white border border-red-300 text-red-700 hover:bg-red-50 rounded-lg inline-flex items-center gap-2"
                  data-testid="cancel-reservation-btn">
            <RotateCcw className="w-4 h-4" /> Rezervasyonu İptal Et / Stoğa Geri Al
          </button>
        )}
        {!isLocked && unit.status !== "Kapora Alındı" && (
          <button onClick={() => setShowDeposit(true)}
                  className="px-4 py-2 text-sm bg-amber-500 hover:bg-amber-600 text-white rounded-lg inline-flex items-center gap-2"
                  data-testid="deposit-btn">
            <Wallet className="w-4 h-4" /> Kapora Al
          </button>
        )}
        {unit.status === "Kapora Alındı" && canCancelDeposit && (
          <button onClick={() => setShowCancelDeposit(true)}
                  className="px-4 py-2 text-sm bg-white border border-red-300 text-red-700 hover:bg-red-50 rounded-lg inline-flex items-center gap-2"
                  data-testid="cancel-deposit-btn">
            <Flame className="w-4 h-4" /> Kaporayı İptal Et / Stoğa Geri Al
          </button>
        )}
        {!isLocked && (
          <button onClick={() => setShowSale(true)}
                  className="px-4 py-2 text-sm bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg inline-flex items-center gap-2"
                  data-testid="sale-btn">
            <ShoppingCart className="w-4 h-4" /> Satış Yap
          </button>
        )}
        {isAdmin && unit.status === "Satıldı" && activeSale && (
          <button onClick={() => setShowCancelSale(true)}
                  className="px-4 py-2 text-sm bg-white border border-red-300 text-red-700 hover:bg-red-50 rounded-lg inline-flex items-center gap-2"
                  data-testid="cancel-sale-btn">
            <RotateCcw className="w-4 h-4" /> Satışı İptal Et / Stoğa Geri Döndür
          </button>
        )}
      </div>

      {hasMedia && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="unit-media-section">
          <button type="button"
                  onClick={() => setMediaOpen((open) => !open)}
                  className="w-full px-4 py-3 flex items-center justify-between gap-3 text-left hover:bg-slate-50">
            <div>
              <div className="text-sm font-semibold text-slate-800">Plan, Görsel ve Video</div>
              <div className="text-xs text-slate-500 mt-0.5">
                {unitImages.length} görsel
                {planLinks.length > 0 ? ` • ${planLinks.length} plan` : ""}
                {unit.video_url ? " • video" : ""}
              </div>
            </div>
            <span className="text-lg leading-none text-slate-400">{mediaOpen ? "−" : "+"}</span>
          </button>

          {mediaOpen && (
            <div className="px-4 pb-4 border-t border-slate-100">
              <div className="pt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
                {unitImages.length > 0 && (
                  <MaterialButton
                    title="Örnek Daire Görselleri"
                    detail={`${unitImages.length} görsel`}
                    onClick={() => setMediaViewer({ type: "images", title: "Örnek Daire Görselleri", urls: unitImages })}
                  />
                )}
                {planLinks.map((item) => (
                  <MaterialButton
                    key={item.label}
                    title={item.label}
                    detail="Planı görüntüle"
                    onClick={() => setMediaViewer({ type: "image", title: item.label, url: item.url })}
                  />
                ))}
                {unit.video_url && (
                  <MaterialButton
                    title="Örnek Daire Videosu"
                    detail="Videoyu görüntüle"
                    onClick={() => setMediaViewer({ type: "video", title: "Örnek Daire Videosu", url: unit.video_url })}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {mediaViewer && (
        <MediaViewer viewer={mediaViewer} onClose={() => setMediaViewer(null)} />
      )}

      <Section
        title={`Teklif Geçmişi (${offers.length})`}
        testid="offer-history-section"
        collapsible={offers.length > 2}
        expanded={isListExpanded("offers")}
        hiddenCount={Math.max(offers.length - 2, 0)}
        onToggle={() => toggleList("offers")}
      >
        {offers.length === 0 ? <Empty>Henüz teklif kaydı yok</Empty> : (
          <ul className={recordListClass("offers", offers)}>
            {visibleRecords("offers", offers).map((offer) => (
              <li key={offer.id} className="px-4 py-3 text-sm" data-testid={`offer-log-${offer.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="font-semibold text-slate-900">
                      {offer.customer_id ? (
                        <Link to={`/customers/${offer.customer_id}`} className="hover:text-amber-600">
                          {offer.customer_name || "Kayıtlı müşteri"}
                        </Link>
                      ) : (
                        offer.customer_name || "Kayıtsız alıcı"
                      )}
                    </div>
                    <div className="mt-1 text-xs text-slate-500">
                      {offer.phone || "Telefon yok"} • {offer.sales_agent_name || "Satışçı"} • {offer.channel || "WhatsApp"}
                    </div>
                    <div className="mt-1 text-xs font-semibold text-slate-600">
                      {offer.delivery_status === "Gönderildi" ? "Gönderildi (manuel onay)" : offer.delivery_status || "Gönderim durumu doğrulanmadı"}
                      {offer.sent_at ? ` · ${new Date(offer.sent_at).toLocaleString("tr-TR")}` : ""}
                    </div>
                    {offer.valid_until && (
                      <div className="mt-1 text-xs text-slate-500">Geçerlilik: {offer.valid_until}</div>
                    )}
                    {offer.pdf_link_expires_at && (
                      <div className="mt-1 text-xs text-slate-500">
                        PDF bağlantısı: {offer.pdf_revoked_at ? "iptal edildi" : new Date(offer.pdf_link_expires_at) > new Date() ? "aktif" : "süresi doldu"}
                        {" · "}{new Date(offer.pdf_link_expires_at).toLocaleString("tr-TR")}
                      </div>
                    )}
                    {offer.message && (
                      <button type="button"
                              onClick={() => setExpandedOfferId((current) => current === offer.id ? "" : offer.id)}
                              className="mt-2 text-xs font-semibold text-amber-700 hover:text-amber-800"
                              data-testid={`toggle-offer-message-${offer.id}`}>
                        {expandedOfferId === offer.id ? "Mesajı gizle" : "Mesajı göster"}
                      </button>
                    )}
                    <Link to={`/offers/${offer.id}/print`} target="_blank"
                          className="mt-2 ml-3 inline-flex items-center gap-1 text-xs font-semibold text-slate-700 hover:text-amber-700">
                      <Printer className="w-3 h-3" /> PDF / Yazdır
                    </Link>
                    {offer.delivery_status === "Hazırlandı" && (
                      <button type="button" disabled={offerBusy} onClick={() => confirmOfferSent(offer.id)}
                              className="mt-2 ml-3 text-xs font-semibold text-emerald-700 hover:underline">
                        Gönderildi olarak işaretle
                      </button>
                    )}
                    {offer.pdf_link_expires_at && !offer.pdf_revoked_at && new Date(offer.pdf_link_expires_at) > new Date() && (
                      <button type="button" disabled={offerBusy} onClick={() => revokeOfferPdf(offer.id)}
                              className="mt-2 ml-3 text-xs font-semibold text-red-700 hover:underline">
                        PDF bağlantısını kapat
                      </button>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-bold text-amber-700">
                      {offer.include_price ? formatPrice(offer.offered_price, offer.currency) : "Fiyatsız"}
                    </div>
                    <div className="mt-1 text-xs text-slate-400">
                      {(offer.created_at || "").slice(0, 19).replace("T", " ")}
                    </div>
                  </div>
                </div>
                {expandedOfferId === offer.id && offer.message && (
                  <div className="mt-3 space-y-2">
                    <pre className="whitespace-pre-wrap rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs leading-relaxed text-slate-700 font-mono">
                      {offer.message}
                    </pre>
                    {offer.conversation_note && (
                      <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs leading-relaxed text-amber-900">
                        <div className="font-semibold mb-1">Görüşme notu</div>
                        <div>{offer.conversation_note}</div>
                        {offer.next_followup_at && <div className="mt-1 text-amber-700">Takip: {offer.next_followup_at}</div>}
                      </div>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </Section>

      {/* Interests */}
      <Section
        title={`İlgilenen Müşteriler (${interests.length})`}
        testid="interests-section"
        collapsible={interests.length > 2}
        expanded={isListExpanded("interests")}
        hiddenCount={Math.max(interests.length - 2, 0)}
        onToggle={() => toggleList("interests")}
      >
        {interests.length === 0 ? <Empty>Henüz ilgi kaydı yok</Empty> : (
          <ul className={recordListClass("interests", interests)}>
            {visibleRecords("interests", interests).map((i) => (
              <li key={i.id} className="px-4 py-3 flex items-center justify-between text-sm" data-testid={`interest-${i.id}`}>
                <div>
                  <div className="font-medium text-slate-900">
                    {i.customer_id ? <Link to={`/customers/${i.customer_id}`} className="hover:text-amber-600">{i.customer_name}</Link> : <span className="text-slate-500 italic">{i.customer_name}</span>}
                  </div>
                  <div className="text-xs text-slate-500">{i.sales_agent_name} • {i.status}</div>
                  {i.note && <div className="text-xs text-slate-600 mt-1">{i.note}</div>}
                </div>
                <div className="text-xs text-slate-400">{(i.created_at || "").slice(0, 10)}</div>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {/* Notes */}
      <Section
        title={`Görüşme Notları (${notes.length})`}
        collapsible={notes.length > 2}
        expanded={isListExpanded("notes")}
        hiddenCount={Math.max(notes.length - 2, 0)}
        onToggle={() => toggleList("notes")}
      >
        {notes.length === 0 ? <Empty>Henüz not yok</Empty> : (
          <ul className={recordListClass("notes", notes)}>
            {visibleRecords("notes", notes).map((n) => (
              <li key={n.id} className="px-4 py-3 text-sm">
                <div className="flex items-center justify-between">
                  <div className="font-medium text-slate-900">
                    {n.customer_id ? (
                      <Link to={`/customers/${n.customer_id}`} className="hover:text-amber-600">{n.customer_name || "Müşteri"}</Link>
                    ) : (
                      n.customer_name || "Müşteri"
                    )}
                    <span className="text-slate-400"> • </span>
                    {n.channel} • {n.result}
                  </div>
                  <div className="text-xs text-slate-400">{(n.created_at || "").slice(0, 10)}</div>
                </div>
                <div className="text-slate-700 mt-1">{n.note}</div>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {/* Modals */}
      {showInterest && (
        <Modal onClose={() => setShowInterest(false)} title="İlgi Kaydı Ekle">
          <form onSubmit={createInterest} className="space-y-3" data-testid="interest-form">
            <CustomerSelect customers={myCustomers} value={interestForm.customer_id}
                            onChange={(v) => setInterestForm({ ...interestForm, customer_id: v })} required />
            <Field label="Durum">
              <select className="input" value={interestForm.status} onChange={(e) => setInterestForm({ ...interestForm, status: e.target.value })}>
                {["İlgileniyor", "Plan Gösterildi", "Fiyat Verildi", "Randevu Verildi", "Vazgeçti"].map((s) => <option key={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Not"><textarea rows={2} className="input resize-none" value={interestForm.note} onChange={(e) => setInterestForm({ ...interestForm, note: e.target.value })} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Sonraki Takip Tarihi">
                <input type="date" className="input" value={interestForm.next_followup_at?.slice(0, 10) || ""}
                       onChange={(e) => setInterestForm({ ...interestForm, next_followup_at: e.target.value })}
                       data-testid="interest-next-followup" />
              </Field>
              <Field label="Hatırlatma Konusu">
                <input className="input" value={interestForm.followup_subject}
                       onChange={(e) => setInterestForm({ ...interestForm, followup_subject: e.target.value })}
                       placeholder="Müşteriye plan gönder" />
              </Field>
            </div>
            <SubmitRow onCancel={() => setShowInterest(false)} testid="submit-interest" />
          </form>
        </Modal>
      )}

      {showReserve && (
        <Modal onClose={() => setShowReserve(false)} title="Rezervasyon Oluştur">
          <form onSubmit={createReservation} className="space-y-3" data-testid="reservation-form">
            <CustomerSelect customers={myCustomers} value={reserveForm.customer_id}
                            onChange={(v) => setReserveForm({ ...reserveForm, customer_id: v })} required />
            <Field label="Geçerlilik Tarihi"><input type="date" className="input" value={reserveForm.valid_until?.slice(0, 10)}
                                                    onChange={(e) => setReserveForm({ ...reserveForm, valid_until: e.target.value })} /></Field>
            <Field label="Not"><textarea rows={2} className="input resize-none" value={reserveForm.note} onChange={(e) => setReserveForm({ ...reserveForm, note: e.target.value })} /></Field>
            <SubmitRow onCancel={() => setShowReserve(false)} testid="submit-reservation" />
          </form>
        </Modal>
      )}

      {showDeposit && (
        <Modal onClose={() => setShowDeposit(false)} title="Kapora Al">
          <form onSubmit={createDeposit} className="space-y-3" data-testid="deposit-form">
            {lockedCustomer ? (
              <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-sm" data-testid="locked-customer-info">
                <div className="text-xs uppercase tracking-wider text-blue-700 font-semibold">Aktif Rezervasyon</div>
                <div className="font-semibold text-slate-900">{lockedCustomer.full_name}</div>
                <div className="text-xs text-slate-600 mt-1">Bu bağımsız bölüm bu müşteri adına rezerve edilmiştir. Kapora sadece bu müşteriden alınabilir.</div>
              </div>
            ) : (
              <CustomerSelect customers={myCustomers} value={depositForm.customer_id}
                              onChange={(v) => setDepositForm({ ...depositForm, customer_id: v })} required />
            )}
            <div className="grid grid-cols-2 gap-3">
              <Field label="Tutar" required><input type="number" required className="input" value={depositForm.amount} onChange={(e) => setDepositForm({ ...depositForm, amount: e.target.value })} data-testid="deposit-amount" /></Field>
              <Field label="Para Birimi">
                <select className="input" value={depositForm.currency} onChange={(e) => setDepositForm({ ...depositForm, currency: e.target.value })}>
                  {["TRY", "USD", "EUR"].map((c) => <option key={c}>{c}</option>)}
                </select>
              </Field>
            </div>
            <Field label="Not"><textarea rows={2} className="input resize-none" value={depositForm.note} onChange={(e) => setDepositForm({ ...depositForm, note: e.target.value })} /></Field>
            <SubmitRow onCancel={() => setShowDeposit(false)} testid="submit-deposit" />
          </form>
        </Modal>
      )}

      {showSale && (
        <Modal onClose={() => setShowSale(false)} title="Satış Yap">
          <form onSubmit={createSale} className="space-y-3" data-testid="sale-form">
            <SalePaymentPreset basePrice={listPrice} currency={saleForm.currency} initialPresetId={saleForm.payment_preset_id} onClear={() => setSaleForm(v => ({ ...v, payment_preset_id: null }))} onApply={(preset, price) => setSaleForm(v => ({ ...v, sale_price: price, payment_preset_id: preset.id, payment_type: "Vadeli / Taksitli" }))} />
            {lockedCustomer ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 text-sm" data-testid="locked-customer-info-sale">
                <div className="text-xs uppercase tracking-wider text-emerald-700 font-semibold">
                  {context?.active_deposits?.length ? "Kapora Veren Müşteri" : "Rezervasyon Müşterisi"}
                </div>
                <div className="font-semibold text-slate-900">{lockedCustomer.full_name}</div>
                <div className="text-xs text-slate-600 mt-1">Satış sadece bu müşteriye yapılabilir.</div>
              </div>
            ) : (
              <CustomerSelect customers={myCustomers} value={saleForm.customer_id}
                              onChange={(v) => setSaleForm({ ...saleForm, customer_id: v })} required />
            )}
            <div className="grid grid-cols-2 gap-3">
              <Field label="Satış Fiyatı" required><input type="number" required className="input" value={saleForm.sale_price} onChange={(e) => setSaleForm({ ...saleForm, sale_price: e.target.value })} data-testid="sale-price" /></Field>
              <Field label="Para Birimi">
                <select className="input" value={saleForm.currency} onChange={(e) => setSaleForm({ ...saleForm, currency: e.target.value })}>
                  {["TRY", "USD", "EUR"].map((c) => <option key={c}>{c}</option>)}
                </select>
              </Field>
            </div>
            {depositTotal > 0 && (
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm space-y-1" data-testid="sale-calc">
                <Row k="Liste fiyatı" v={formatPrice(listPrice, unit.currency)} />
                <Row k="Alınan kapora toplamı" v={formatPrice(depositTotal, context?.deposit_currency || unit.currency)} accent="text-amber-700" />
                <Row k="Satış fiyatı" v={formatPrice(salePriceNum, saleForm.currency)} />
                <div className="border-t border-slate-200 pt-1 mt-1">
                  <Row k="Kalan ödeme" v={formatPrice(remaining, saleForm.currency)} bold accent="text-emerald-700" testid="sale-remaining" />
                </div>
              </div>
            )}
            <Field label="Ödeme Tipi">
              <select className="input" value={saleForm.payment_type} onChange={(e) => setSaleForm({ ...saleForm, payment_type: e.target.value })} data-testid="sale-payment-type">
                {["Peşin", "Nakit / Havale", "Vadeli / Taksitli", "Araç Takaslı", "Arsa Takaslı", "Karma", "Diğer"].map((p) => <option key={p}>{p}</option>)}
              </select>
            </Field>
            <Field label="Not"><textarea rows={2} className="input resize-none" value={saleForm.note} onChange={(e) => setSaleForm({ ...saleForm, note: e.target.value })} /></Field>
            <SubmitRow onCancel={() => setShowSale(false)} testid="submit-sale" />
          </form>
        </Modal>
      )}

      {showCancelReservation && (
        <Modal onClose={() => setShowCancelReservation(false)} title="Rezervasyonu İptal Et">
          <form onSubmit={cancelReservation} className="space-y-3" data-testid="cancel-reservation-form">
            <Field label="İptal Nedeni" required>
              <textarea required rows={3} className="input resize-none" value={cancelResForm.reason}
                        onChange={(e) => setCancelResForm({ reason: e.target.value })} data-testid="cancel-res-reason" />
            </Field>
            <SubmitRow onCancel={() => setShowCancelReservation(false)} testid="submit-cancel-reservation" submitLabel="İptal Et" danger />
          </form>
        </Modal>
      )}

      {showCancelDeposit && (
        <Modal onClose={() => setShowCancelDeposit(false)} title="Kaporayı İptal Et / Stoğa Geri Al">
          <form onSubmit={cancelDeposit} className="space-y-3" data-testid="cancel-deposit-form">
            <Field label="İptal Nedeni" required>
              <textarea required rows={2} className="input resize-none" value={cancelDepForm.reason}
                        onChange={(e) => setCancelDepForm({ ...cancelDepForm, reason: e.target.value })} data-testid="cancel-dep-reason" />
            </Field>
            <Field label="Açıklama">
              <textarea rows={2} className="input resize-none" value={cancelDepForm.description}
                        onChange={(e) => setCancelDepForm({ ...cancelDepForm, description: e.target.value })} />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Kapora İade Durumu">
                <select className="input" value={cancelDepForm.refunded ? "true" : "false"}
                        onChange={(e) => setCancelDepForm({ ...cancelDepForm, refunded: e.target.value === "true" })}
                        data-testid="cancel-dep-refunded">
                  <option value="false">Yakıldı (iade edilmedi)</option>
                  <option value="true">İade Edildi</option>
                </select>
              </Field>
              <Field label="Yeni Bölüm Durumu">
                <select className="input" value={cancelDepForm.new_unit_status}
                        onChange={(e) => setCancelDepForm({ ...cancelDepForm, new_unit_status: e.target.value })}
                        data-testid="cancel-dep-new-status">
                  <option value="Satışta">Satışta</option>
                  <option value="Rezerve">Rezerve</option>
                </select>
              </Field>
            </div>
            <Field label="Müşteri Yeni Durum (opsiyonel)">
              <select className="input" value={cancelDepForm.new_customer_status}
                      onChange={(e) => setCancelDepForm({ ...cancelDepForm, new_customer_status: e.target.value })}>
                <option value="">Değiştirme</option>
                {["İlgisiz", "İptal", "Vazgeçti"].map((s) => <option key={s}>{s}</option>)}
              </select>
            </Field>
            <SubmitRow onCancel={() => setShowCancelDeposit(false)} testid="submit-cancel-deposit" submitLabel="Kaporayı Kapat" danger />
          </form>
        </Modal>
      )}

      {showEditUnit && (
        <Modal onClose={() => setShowEditUnit(false)} title={`Düzenle: ${unit.unit_no}`} wide>
          <form onSubmit={submitEditUnit} className="space-y-3" data-testid="edit-unit-form">
            {editStructureLocked && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                Bu bölümde aktif işlem geçmişi var. Daire no, blok, kat ve durum alanları güvenlik için kilitli.
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <Field label="Daire / Bölüm No">
                <input className="input" value={editForm.unit_no || ""}
                       disabled={editStructureLocked}
                       onChange={(e) => setEditForm({ ...editForm, unit_no: e.target.value })} />
              </Field>
              <Field label="Blok">
                <select className="input" value={editForm.block_id || ""}
                        disabled={editStructureLocked}
                        onChange={(e) => setEditForm({ ...editForm, block_id: e.target.value, floor_id: "" })}>
                  <option value="">Blok seçin</option>
                  {editBlocks.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </Field>
              <Field label="Kat">
                <select className="input" value={editForm.floor_id || ""}
                        disabled={editStructureLocked || !editForm.block_id}
                        onChange={(e) => setEditForm({ ...editForm, floor_id: e.target.value })}>
                  <option value="">Kat seçin</option>
                  {editFloors.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                </select>
              </Field>
              <Field label="Bölüm Tipi">
                <select className="input" value={UNIT_TYPES.includes(editForm.unit_type) ? editForm.unit_type : "Diğer"} onChange={(e) => setEditForm({ ...editForm, unit_type: e.target.value })}>
                  {UNIT_TYPES.map((t) => <option key={t}>{t}</option>)}
                </select>
                {editForm.unit_type === "Diğer" || !UNIT_TYPES.includes(editForm.unit_type) ? (
                  <input className="input mt-2" placeholder="Bölüm tipini yazın"
                         value={UNIT_TYPES.includes(editForm.unit_type) ? "" : editForm.unit_type || ""}
                         onChange={(e) => setEditForm({ ...editForm, unit_type: e.target.value || "Diğer" })} />
                ) : null}
              </Field>
              <Field label="Oda Tipi">
                <select className="input" value={ROOM_TYPES.includes(editForm.room_type) ? editForm.room_type : "Diğer"} onChange={(e) => setEditForm({ ...editForm, room_type: e.target.value })}>
                  {ROOM_TYPES.map((t) => <option key={t}>{t}</option>)}
                </select>
                {editForm.room_type === "Diğer" || !ROOM_TYPES.includes(editForm.room_type) ? (
                  <input className="input mt-2" placeholder="Oda tipini yazın"
                         value={ROOM_TYPES.includes(editForm.room_type) ? "" : editForm.room_type || ""}
                         onChange={(e) => setEditForm({ ...editForm, room_type: e.target.value || "Diğer" })} />
                ) : null}
              </Field>
              <Field label="Plan / Daire Tipi">
                <input className="input" placeholder="A Tipi, B Tipi, Loft Tipi"
                       value={editForm.layout_type || ""}
                       onChange={(e) => setEditForm({ ...editForm, layout_type: e.target.value })} />
              </Field>
              <Field label="Net m²"><input type="number" step="0.01" className="input" value={editForm.net_area || 0} onChange={(e) => setEditForm({ ...editForm, net_area: e.target.value })} /></Field>
              <Field label="Brüt m²"><input type="number" step="0.01" className="input" value={editForm.gross_area || 0} onChange={(e) => setEditForm({ ...editForm, gross_area: e.target.value })} /></Field>
              <Field label="Cephe">
                <select className="input" value={editForm.aspect || ""} onChange={(e) => setEditForm({ ...editForm, aspect: e.target.value })}>
                  {ASPECTS.map((t) => <option key={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Manzara">
                <select className="input" value={editForm.view || ""} onChange={(e) => setEditForm({ ...editForm, view: e.target.value })}>
                  {VIEWS.map((t) => <option key={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Kat Konumu">
                <select className="input" value={editForm.floor_position || ""} onChange={(e) => setEditForm({ ...editForm, floor_position: e.target.value })}>
                  {FLOOR_POSITIONS.map((t) => <option key={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Para Birimi">
                <select className="input" value={editForm.currency || "TRY"} onChange={(e) => setEditForm({ ...editForm, currency: e.target.value })}>
                  {CURRENCIES.map((t) => <option key={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Fiyat"><input type="number" className="input" value={editForm.price || 0} onChange={(e) => setEditForm({ ...editForm, price: e.target.value })} data-testid="edit-price" /></Field>
              <Field label="Durum">
                <select className="input" value={editForm.status || "Satışta"}
                        disabled={editStructureLocked}
                        onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                        data-testid="edit-status">
                  {(editStructureLocked ? [unit.status] : ["Satışta", "Güncellemede", "Pasif"]).map((t) => <option key={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Sorumlu Satış Personeli">
                <select className="input" value={editForm.sales_agent_id || ""} onChange={(e) => setEditForm({ ...editForm, sales_agent_id: e.target.value })}>
                  <option value="">— Atama yok —</option>
                  {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </Field>
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">Daire / Bölüm Planı</div>
                <ImageUploadField value={editForm.plan_url} onChange={(url) => setEditForm((form) => ({ ...form, plan_url: url }))} />
              </div>
              <Field label="Video URL"><input className="input" value={editForm.video_url || ""} onChange={(e) => setEditForm({ ...editForm, video_url: e.target.value })} /></Field>
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">Kat / Konum Planı</div>
                <ImageUploadField value={editForm.seating_plan_url} onChange={(url) => setEditForm((form) => ({ ...form, seating_plan_url: url }))} />
              </div>
              <div className="col-span-2">
                <Field label="Örnek Daire Görselleri">
                  <textarea rows={3} className="input resize-none" placeholder="Her satıra bir görsel URL'si"
                            value={editForm.image_urls_text || ""}
                            onChange={(e) => setEditForm({ ...editForm, image_urls_text: e.target.value })} />
                </Field>
              </div>
              <div className="col-span-2">
                <Field label="Not"><textarea rows={2} className="input resize-none" value={editForm.note || ""} onChange={(e) => setEditForm({ ...editForm, note: e.target.value })} /></Field>
              </div>
            </div>
            <SubmitRow onCancel={() => setShowEditUnit(false)} testid="submit-edit-unit" />
          </form>
        </Modal>
      )}

      {showOfferModal && (
        <Modal onClose={() => setShowOfferModal(false)} title="WhatsApp Teklif Hazırla" wide>
          <div className="space-y-4" data-testid="offer-modal">
            <div className="grid md:grid-cols-2 gap-3">
              <Field label="Kayıtlı Müşteri">
                <select className="input" value={offerForm.customer_id}
                        onChange={(e) => selectOfferCustomer(e.target.value)}
                        data-testid="offer-customer-select">
                  <option value="">Kayıtlı müşteri seçilmedi</option>
                  {myCustomers.map((c) => (
                    <option key={c.id} value={c.id}>{c.full_name}{c.phone ? ` — ${c.phone}` : ""}</option>
                  ))}
                </select>
              </Field>
              <Field label="Müşteri Adı">
                <input className="input" placeholder="Ahmet Bey, Ayşe Hanım..."
                       value={offerForm.customer_name}
                       onChange={(e) => updateOfferForm({ customer_name: e.target.value })}
                       data-testid="offer-customer-name" />
              </Field>
              <Field label="WhatsApp Telefonu">
                <div className="grid grid-cols-[74px_1fr] gap-2">
                  <input className="input"
                         placeholder="+90"
                         value={offerForm.phone_country_code ? `+${offerForm.phone_country_code}` : ""}
                         onChange={(e) => updateOfferForm({ phone_country_code: e.target.value.replace(/\D/g, "") })}
                         data-testid="offer-phone-country" />
                  <input className="input" placeholder="500 000 00 00"
                         value={offerForm.phone_local}
                         onChange={(e) => updateOfferForm({ phone_local: e.target.value })}
                         data-testid="offer-phone" />
                </div>
              </Field>
              <Field label="Teklif Fiyatı">
                <input type="number" className="input"
                       value={offerForm.offer_price}
                       onChange={(e) => updateOfferForm({ offer_price: e.target.value })}
                       data-testid="offer-price" />
              </Field>
              <Field label="Geçerlilik Tarihi">
                <input type="date" className="input"
                       value={offerForm.valid_until}
                       onChange={(e) => updateOfferForm({ valid_until: e.target.value })}
                       data-testid="offer-valid-until" />
              </Field>
            </div>
            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={offerForm.include_price}
                     onChange={(e) => updateOfferForm({ include_price: e.target.checked })}
                     data-testid="offer-include-price" />
              Fiyatı teklif metninde göster
            </label>
            <div>
              <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={offerForm.include_pdf_link}
                       onChange={(e) => setOfferForm((form) => ({ ...form, include_pdf_link: e.target.checked }))}
                       data-testid="offer-include-pdf-link" />
                Süreli teklif PDF bağlantısı ekle
              </label>
              {localPdfLink && <div className="mt-1 text-xs text-amber-700">
                Yerel bağlantı yalnızca bu bilgisayarda açılır; müşteriye göndermek için herkese açık sunucu gerekir.
              </div>}
            </div>
            <Field label="Teklif Metni">
              <textarea rows={10} className="input resize-none font-mono text-xs leading-relaxed"
                        value={offerForm.message}
                        onChange={(e) => setOfferForm((form) => ({ ...form, message: e.target.value }))}
                        data-testid="offer-message" />
            </Field>
            <div className="grid md:grid-cols-2 gap-3">
              <Field label="Görüşme Notu">
                <textarea rows={3} className="input resize-none"
                          placeholder="Müşteri düşünecek, eşine danışacak, pazartesi aranacak..."
                          value={offerForm.conversation_note}
                          onChange={(e) => setOfferForm((form) => ({ ...form, conversation_note: e.target.value }))}
                          data-testid="offer-conversation-note" />
              </Field>
              <Field label="Sonraki Takip Tarihi">
                <input type="date" className="input"
                       value={offerForm.next_followup_at}
                       onChange={(e) => setOfferForm((form) => ({ ...form, next_followup_at: e.target.value }))}
                       data-testid="offer-next-followup" />
              </Field>
            </div>
            {preparedOfferId && (
              <div className="flex flex-wrap items-center justify-between gap-2 border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
                <span>Teklif hazırlandı. Mesajı gerçekten gönderdikten sonra kaydedin.</span>
                <button type="button" disabled={offerBusy} onClick={() => confirmOfferSent(preparedOfferId)}
                        className="font-semibold underline">Gönderdim, kaydet</button>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setShowOfferModal(false)}
                      className="px-4 py-2 text-sm bg-white border border-slate-200 rounded-lg">
                Vazgeç
              </button>
              <button type="button" onClick={copyOfferText} disabled={offerBusy}
                      className="px-4 py-2 text-sm bg-white border border-slate-200 rounded-lg inline-flex items-center gap-2"
                      data-testid="copy-offer-text">
                <Copy className="w-4 h-4" /> Metni Kopyala
              </button>
              <button type="button" onClick={openWhatsappOffer} disabled={offerBusy}
                      className="px-4 py-2 text-sm bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg inline-flex items-center gap-2"
                      data-testid="send-whatsapp-offer">
                <MessageCircle className="w-4 h-4" /> WhatsApp'ta Aç
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Activity History */}
      <Section
        title={`İşlem Geçmişi (${activity.length})`}
        testid="activity-history"
        collapsible={activity.length > 2}
        expanded={isListExpanded("activity")}
        hiddenCount={Math.max(activity.length - 2, 0)}
        onToggle={() => toggleList("activity")}
      >
        {activity.length === 0 ? <Empty>Henüz işlem yok</Empty> : (
          <ul className={recordListClass("activity", activity)}>
            {visibleRecords("activity", activity).map((a) => (
              <li key={a.id} className="px-4 py-3 text-sm" data-testid={`activity-${a.id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs text-slate-500">{activityLabel(a.action_type)}</div>
                    <div className="text-slate-900 mt-0.5">{a.description}</div>
                  </div>
                  <div className="text-xs text-slate-400 text-right">
                    <div>{(a.created_at || "").slice(0, 19).replace("T", " ")}</div>
                    <div>{a.user_name}</div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {showCancelSale && (
        <Modal onClose={() => setShowCancelSale(false)} title="Satışı İptal Et">
          <form onSubmit={cancelSale} className="space-y-3" data-testid="cancel-sale-form">
            <Field label="İptal Nedeni" required>
              <textarea required rows={3} className="input resize-none" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} data-testid="cancel-reason-input" />
            </Field>
            <SubmitRow onCancel={() => setShowCancelSale(false)} testid="submit-cancel-sale" submitLabel="İptal Et" danger />
          </form>
        </Modal>
      )}
    </div>
  );
}

function Info({ l, v, highlight, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid={testid}>
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{l}</div>
      <div className={`mt-1 font-semibold ${highlight ? "text-amber-600 text-xl" : "text-slate-900"}`}>{v}</div>
    </div>
  );
}
function Section({ title, children, testid, collapsible = false, expanded = false, hiddenCount = 0, onToggle }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={testid}>
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-3">
        <div className="text-sm font-semibold text-slate-700">{title}</div>
        {collapsible && (
          <button type="button"
                  onClick={onToggle}
                  className="inline-flex items-center gap-2 text-xs font-semibold text-slate-500 hover:text-amber-700"
                  aria-expanded={expanded}>
            <span>{expanded ? "Kapat" : `${hiddenCount} kayıt daha`}</span>
            <span className="text-base leading-none">{expanded ? "⌃" : "⌄"}</span>
          </button>
        )}
      </div>
      {children}
    </div>
  );
}
function Empty({ children }) {
  return <div className="px-4 py-8 text-sm text-slate-400 text-center">{children}</div>;
}
function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">{label} {required && <span className="text-red-500">*</span>}</div>
      {children}
    </label>
  );
}
function Modal({ title, children, onClose, wide }) {
  return (
    <div className="fixed inset-0 z-50 !mt-0 flex items-start justify-center overflow-y-auto bg-black/40 p-4" onClick={onClose} data-testid="modal-backdrop">
      <div className={`my-auto w-full rounded-xl bg-white p-6 ${wide ? "max-w-2xl" : "max-w-md"}`} onClick={(e) => e.stopPropagation()}>
        <div className="text-lg font-semibold text-slate-900 mb-4 font-display">{title}</div>
        {children}
      </div>
    </div>
  );
}

function MaterialButton({ title, detail, onClick }) {
  return (
    <button type="button"
            onClick={onClick}
            className="text-left rounded-lg border border-slate-200 bg-slate-50 hover:bg-white hover:border-amber-300 px-3 py-3 transition">
      <div className="text-sm font-semibold text-slate-900">{title}</div>
      <div className="text-xs text-slate-500 mt-1">{detail}</div>
    </button>
  );
}

function MediaViewer({ viewer, onClose }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const urls = viewer.urls || [];
  const activeUrl = urls[activeIndex] || "";
  const goPrev = () => setActiveIndex((i) => (i === 0 ? urls.length - 1 : i - 1));
  const goNext = () => setActiveIndex((i) => (i === urls.length - 1 ? 0 : i + 1));

  return createPortal(
    <div role="dialog" aria-modal="true" aria-label={viewer.title} className="fixed inset-0 z-50 !mt-0 flex items-center justify-center bg-black/60 p-4 overflow-y-auto" onClick={onClose} onKeyDown={e => { if (e.key === "Escape") onClose(); }} data-testid="media-viewer">
      <div className="bg-white rounded-xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between gap-3">
          <div>
            <div className="text-base font-semibold text-slate-900">{viewer.title}</div>
            {viewer.type === "images" && urls.length > 0 && (
              <div className="text-xs text-slate-500">{activeIndex + 1} / {urls.length}</div>
            )}
          </div>
          <button type="button" autoFocus onClick={onClose} className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg hover:bg-slate-50">
            Kapat
          </button>
        </div>
        <div className="p-4 overflow-y-auto">
          {viewer.type === "images" && (
            <div className="space-y-3">
              <div className="relative rounded-xl overflow-hidden bg-slate-100 border border-slate-200">
                <div className="h-[58vh] min-h-[360px] flex items-center justify-center">
                  {activeUrl ? (
                    <img src={activeUrl} alt="" className="w-full h-full object-contain" />
                  ) : (
                    <div className="text-sm text-slate-400">Görsel yok</div>
                  )}
                </div>
                {urls.length > 1 && (
                  <>
                    <button type="button" onClick={goPrev}
                            className="absolute left-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/90 border border-slate-200 text-xl leading-none shadow hover:bg-white"
                            aria-label="Önceki görsel">
                      ‹
                    </button>
                    <button type="button" onClick={goNext}
                            className="absolute right-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/90 border border-slate-200 text-xl leading-none shadow hover:bg-white"
                            aria-label="Sonraki görsel">
                      ›
                    </button>
                  </>
                )}
              </div>
              {urls.length > 1 && (
                <div className="flex gap-2 overflow-x-auto pb-1">
                  {urls.map((url, index) => (
                    <button key={`${url}-${index}`} type="button"
                            onClick={() => setActiveIndex(index)}
                            className={`h-16 w-24 shrink-0 rounded-lg overflow-hidden border ${index === activeIndex ? "border-amber-500 ring-2 ring-amber-200" : "border-slate-200"}`}>
                      <img src={url} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
              {activeUrl && !activeUrl.startsWith("data:") && (
                <a href={activeUrl} target="_blank" rel="noreferrer" className="inline-flex text-xs font-semibold text-amber-700 hover:text-amber-800">
                  Görseli yeni sekmede aç
                </a>
              )}
            </div>
          )}
          {viewer.type === "image" && (
            <div className="block rounded-xl overflow-hidden bg-slate-100 border border-slate-200">
              <div className="min-h-[360px] max-h-[70vh] flex items-center justify-center">
                <img src={viewer.url} alt={viewer.title} className="w-full max-h-[70vh] object-contain" />
              </div>
            </div>
          )}
          {viewer.type === "video" && (
            <div className="rounded-xl overflow-hidden bg-slate-100 border border-slate-200">
              <div className="aspect-video">
                {viewer.url.includes("youtube.com") || viewer.url.includes("youtu.be") ? (
                  <iframe src={viewer.url} title={viewer.title} className="w-full h-full" allowFullScreen />
                ) : (
                  <video src={viewer.url} controls className="w-full h-full bg-black" />
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>, document.body
  );
}

function Row({ k, v, bold, accent, testid }) {
  return (
    <div className="flex justify-between items-baseline" data-testid={testid}>
      <span className={`text-slate-600 ${bold ? "font-semibold" : ""}`}>{k}</span>
      <span className={`${bold ? "font-bold" : "font-medium"} ${accent || "text-slate-900"}`}>{v}</span>
    </div>
  );
}
function SubmitRow({ onCancel, submitLabel = "Kaydet", testid, danger }) {
  return (
    <div className="flex justify-end gap-2 pt-2">
      <button type="button" onClick={onCancel} className="px-4 py-2 text-sm border border-slate-200 rounded-lg">Vazgeç</button>
      <button type="submit" data-testid={testid}
              className={`px-4 py-2 text-sm text-white rounded-lg ${danger ? "bg-red-600 hover:bg-red-700" : "bg-slate-900 hover:bg-slate-800"}`}>
        {submitLabel}
      </button>
    </div>
  );
}
function CustomerSelect({ customers, value, onChange, required }) {
  return (
    <label className="block">
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">Müşteri {required && <span className="text-red-500">*</span>}</div>
      <select required={required} value={value} onChange={(e) => onChange(e.target.value)} className="input" data-testid="customer-select">
        <option value="">Müşteri seç…</option>
        {customers.map((c) => <option key={c.id} value={c.id}>{c.full_name} — {c.phone}</option>)}
      </select>
    </label>
  );
}
